import React, { useEffect, useState } from 'react';

function formatFieldLabel(key) {
  return key
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function hasOnboardingData(data) {
  return data && typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length > 0;
}

function OnboardingDataFields({ data, compact = false }) {
  return (
    <dl className={compact ? 'space-y-1.5' : 'space-y-3'}>
      {Object.entries(data).map(([key, value]) => (
        <div key={key}>
          <dt className="text-xs font-medium text-content-muted">{formatFieldLabel(key)}</dt>
          <dd className={`text-sm text-content-primary ${compact ? 'mt-0.5' : 'mt-1'}`}>
            {renderOnboardingValue(value)}
          </dd>
        </div>
      ))}
    </dl>
  );
}

function renderOnboardingValue(value) {
  if (value === null || value === undefined) {
    return <span className="text-content-subtle">—</span>;
  }

  if (typeof value === 'boolean') {
    return value ? 'Sim' : 'Não';
  }

  if (Array.isArray(value)) {
    if (value.length === 0) {
      return <span className="text-content-subtle">Nenhum</span>;
    }

    if (value.every((item) => item && typeof item === 'object' && !Array.isArray(item))) {
      return (
        <ul className="space-y-2">
          {value.map((item, index) => (
            <li
              key={index}
              className="rounded-lg border border-slate-200 bg-slate-50/60 px-3 py-2"
            >
              <OnboardingDataFields data={item} compact />
            </li>
          ))}
        </ul>
      );
    }

    return value.map(String).join(', ');
  }

  if (typeof value === 'object') {
    return <OnboardingDataFields data={value} compact />;
  }

  return String(value);
}

function formatOnboardingJson(data) {
  if (!data || typeof data !== 'object') {
    return '{\n}';
  }
  return JSON.stringify(data, null, 2);
}

export default function ContactOnboardingSection({
  onboardingData = null,
  onboardingCompleted = false,
  onSave,
  saving = false,
}) {
  const [editing, setEditing] = useState(false);
  const [draftJson, setDraftJson] = useState(() => formatOnboardingJson(onboardingData));
  const [draftCompleted, setDraftCompleted] = useState(Boolean(onboardingCompleted));
  const [localError, setLocalError] = useState('');

  const hasData = hasOnboardingData(onboardingData);

  useEffect(() => {
    if (!editing) {
      setDraftJson(formatOnboardingJson(onboardingData));
      setDraftCompleted(Boolean(onboardingCompleted));
      setLocalError('');
    }
  }, [onboardingData, onboardingCompleted, editing]);

  const handleStartEdit = () => {
    setDraftJson(formatOnboardingJson(onboardingData));
    setDraftCompleted(Boolean(onboardingCompleted));
    setLocalError('');
    setEditing(true);
  };

  const handleCancel = () => {
    setDraftJson(formatOnboardingJson(onboardingData));
    setDraftCompleted(Boolean(onboardingCompleted));
    setLocalError('');
    setEditing(false);
  };

  const handleSave = async () => {
    if (!onSave || saving) return;

    let parsed;
    try {
      const trimmed = draftJson.trim();
      parsed = trimmed === '' ? null : JSON.parse(trimmed);
    } catch {
      setLocalError('JSON inválido. Corrija o formato antes de salvar.');
      return;
    }

    if (parsed !== null && (typeof parsed !== 'object' || Array.isArray(parsed))) {
      setLocalError('O onboarding_data deve ser um objeto JSON (não array ou valor primitivo).');
      return;
    }

    setLocalError('');
    const ok = await onSave({
      onboardingData: parsed,
      onboardingCompleted: draftCompleted,
    });
    if (ok !== false) {
      setEditing(false);
    }
  };

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-content-primary">Dados do onboarding</h2>
          <p className="text-xs text-content-muted mt-1">
            Informações coletadas durante o onboarding do contato
          </p>
        </div>

        {!editing && (
          <button
            type="button"
            onClick={handleStartEdit}
            className="text-sm text-forge-600 hover:text-forge-800 shrink-0"
          >
            {hasData || onboardingCompleted ? 'Editar' : 'Adicionar dados'}
          </button>
        )}
      </div>

      <div className="surface-card px-4 py-4">
        {editing ? (
          <div className="space-y-3">
            <label className="flex items-center gap-2 text-sm text-content-primary cursor-pointer">
              <input
                type="checkbox"
                checked={draftCompleted}
                onChange={(event) => setDraftCompleted(event.target.checked)}
                disabled={saving}
                className="rounded border-slate-300 text-forge-600 focus:ring-forge-500"
              />
              Onboarding concluído
            </label>

            <div>
              <label
                htmlFor="contact-onboarding-json"
                className="block text-xs font-medium text-content-muted mb-1.5"
              >
                onboarding_data (JSON)
              </label>
              <textarea
                id="contact-onboarding-json"
                value={draftJson}
                onChange={(event) => setDraftJson(event.target.value)}
                rows={12}
                disabled={saving}
                spellCheck={false}
                className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 font-mono text-sm text-content-primary focus:outline-none focus:ring-2 focus:ring-forge-500/30 focus:border-forge-500 disabled:opacity-50"
                placeholder={'{\n  "campo": "valor"\n}'}
              />
            </div>

            {localError && (
              <p className="text-sm text-red-700">{localError}</p>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="btn-primary text-sm px-4 py-2 disabled:opacity-40"
              >
                {saving ? 'Salvando...' : 'Salvar'}
              </button>
              <button
                type="button"
                onClick={handleCancel}
                disabled={saving}
                className="text-sm text-content-muted hover:text-content-primary disabled:opacity-40"
              >
                Cancelar
              </button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-content-muted">Status</span>
              <span
                className={`inline-flex items-center text-xs px-2 py-0.5 rounded-full font-medium ${
                  onboardingCompleted
                    ? 'text-emerald-700 bg-emerald-50'
                    : 'text-amber-700 bg-amber-50'
                }`}
              >
                {onboardingCompleted ? 'Concluído' : 'Pendente'}
              </span>
            </div>

            {hasData ? (
              <OnboardingDataFields data={onboardingData} />
            ) : (
              <p className="text-sm text-content-subtle italic">
                Nenhum dado de onboarding registrado.
              </p>
            )}
          </div>
        )}
      </div>
    </section>
  );
}
